import React, { useRef } from 'react';
import { colour, formatterLong } from '../styles/GlobalStyles';

// Generic SVG line chart for monthly price series. Originally extracted from
// RedfinMarketTrends.js so it can be reused for the ArvWorkspace market-trend
// deep-dive modal. Data shape is `{date: string, price: number}[]`. Tooltip
// wiring is opt-in via the hover/position props — when omitted the chart
// renders cleanly without interactive tooltips.
//
// `containerSelector` controls which ancestor element the tooltip-position
// math measures against. RedfinMarketTrends passes its own wrapper class;
// callers without that wrapper can omit it and the chart falls back to its
// own container ref.
function SimpleLineChart({
    data,
    darkMode,
    trendBucket,
    onPointHover,
    onPointLeave,
    hoveredIndex,
    containerSelector = '.redfin-market-trends-container',
    valueFormatter,
}) {
    // Y-axis label formatter — defaults to currency for backwards compat
    // (RedfinMarketTrends.js relies on the dollar-formatted axis). Callers
    // showing non-price series (homes sold, DOM) pass a custom formatter.
    const formatAxisValue = valueFormatter
        || ((v) => formatterLong.format(v).replace('.00', ''));
    const chartContainerRef = useRef(null);

    if (!data || data.length === 0) return null;

    const validData = data.filter(d => d.price !== null && d.price !== undefined);
    if (validData.length === 0) return null;

    const prices = validData.map(d => d.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;

    const chartWidth = 600;
    const chartHeight = 300;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const graphWidth = chartWidth - padding.left - padding.right;
    const graphHeight = chartHeight - padding.top - padding.bottom;

    const getLineColor = () => {
        if (trendBucket === 'market_up') return colour.jade02;
        if (trendBucket === 'market_down') return colour.orange02;
        return darkMode ? colour.gray03 : colour.gray04;
    };

    const lineColor = getLineColor();
    const gridColor = darkMode ? colour.gray03 : colour.gray02;
    const axisLabelColor = darkMode ? colour.gray04 : colour.gray05;

    const points = validData.map((d, index) => {
        const x = padding.left + (validData.length === 1 ? graphWidth / 2 : (index / (validData.length - 1)) * graphWidth);
        const y = padding.top + graphHeight - ((d.price - minPrice) / priceRange) * graphHeight;
        return { x, y, price: d.price, date: d.date };
    });

    const yAxisSteps = 5;
    const yAxisLabels = [];
    for (let i = 0; i <= yAxisSteps; i++) {
        const value = minPrice + (priceRange * i / yAxisSteps);
        yAxisLabels.push({
            value,
            y: padding.top + graphHeight - (i / yAxisSteps) * graphHeight,
        });
    }

    const pathData = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

    const handleMouseEnter = (index, point) => {
        if (chartContainerRef.current && onPointHover) {
            const containerRect = chartContainerRef.current.getBoundingClientRect();
            const svgRect = chartContainerRef.current.querySelector('svg').getBoundingClientRect();
            const scaleX = svgRect.width / chartWidth;
            const scaleY = svgRect.height / chartHeight;

            const mainContainer = containerSelector
                ? chartContainerRef.current.closest(containerSelector)
                : null;
            const mainContainerRect = mainContainer ? mainContainer.getBoundingClientRect() : containerRect;

            const tooltipX = (point.x * scaleX) + svgRect.left - mainContainerRect.left;
            const tooltipY = (point.y * scaleY) + svgRect.top - mainContainerRect.top;

            onPointHover(index, { x: tooltipX, y: tooltipY });
        }
    };

    return (
        <div className="redfin-market-trends-chart-container" ref={chartContainerRef}>
            <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                preserveAspectRatio="xMidYMid meet"
                className="redfin-market-trends-svg"
            >
                {yAxisLabels.map((label, index) => (
                    <line
                        key={`grid-${index}`}
                        x1={padding.left}
                        y1={label.y}
                        x2={padding.left + graphWidth}
                        y2={label.y}
                        stroke={gridColor}
                        strokeWidth="1"
                        strokeDasharray="4,4"
                        opacity={0.5}
                    />
                ))}

                {yAxisLabels.map((label, index) => (
                    <text
                        key={`y-label-${index}`}
                        x={padding.left - 10}
                        y={label.y + 4}
                        textAnchor="end"
                        fontSize="12"
                        fill={axisLabelColor}
                        className="label-regular"
                    >
                        {formatAxisValue(label.value)}
                    </text>
                ))}

                {validData.map((d, index) => {
                    const x = padding.left + (validData.length === 1 ? graphWidth / 2 : (index / (validData.length - 1)) * graphWidth);
                    const shouldShow = index === 0 || index === validData.length - 1 || (validData.length > 2 && index === Math.floor(validData.length / 2));
                    if (!shouldShow) return null;
                    return (
                        <text
                            key={`x-label-${index}`}
                            x={x}
                            y={chartHeight - padding.bottom + 20}
                            textAnchor="middle"
                            fontSize="12"
                            fill={axisLabelColor}
                            className="label-regular"
                        >
                            {d.date}
                        </text>
                    );
                })}

                <path
                    d={pathData}
                    fill="none"
                    stroke={lineColor}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {points.map((point, index) => {
                    const isHovered = hoveredIndex === index;

                    return (
                        <g key={`point-group-${index}`}>
                            {onPointHover && (
                                <circle
                                    cx={point.x}
                                    cy={point.y}
                                    r="12"
                                    fill="transparent"
                                    className="redfin-market-trends-point-hit-area"
                                    onMouseEnter={() => handleMouseEnter(index, point)}
                                    onMouseLeave={() => onPointLeave && onPointLeave()}
                                    pointerEvents="all"
                                />
                            )}
                            <circle
                                cx={point.x}
                                cy={point.y}
                                r={isHovered ? "6" : "4"}
                                fill={lineColor}
                                stroke={colour.gray00}
                                strokeWidth="2"
                                className="redfin-market-trends-point"
                                pointerEvents="none"
                            />
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

export default SimpleLineChart;
